import { intlFormat as formatDate } from 'date-fns/intlFormat';
import { isValid as isValidDate } from 'date-fns/isValid';
import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';

import type { LanguageKey } from '~/shared/internationalization';

export type FormatByLocaleOptions = {
    readonly dateStringToFormat: Maybe<string>;
    readonly locale: LanguageKey;
};

export const formatDateByLocale = (options: FormatByLocaleOptions): Result<string, Error> => {
    const { dateStringToFormat, locale } = options;

    const noDateStringProvidedError = Result.err<string, Error>(new Error('No date string provided'));

    return dateStringToFormat.mapOr(noDateStringProvidedError, (dateToFormatValue) => {
        const parsedDate = new Date(dateToFormatValue);

        if (!isValidDate(parsedDate)) {
            return Result.err<string, Error>(new Error(`Invalid date to format: "${dateToFormatValue}"`));
        }

        const formattedDate = formatDate(
            parsedDate,
            {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
            },
            { locale },
        );

        return Result.ok(formattedDate);
    });
};

export const formatDateWithShortMonthByLocale = (options: FormatByLocaleOptions): Result<string, Error> => {
    const { dateStringToFormat, locale } = options;
    const noDateStringProvidedError = Result.err<string, Error>(new Error('No date string provided'));

    return dateStringToFormat.mapOr(noDateStringProvidedError, (dateToFormatValue) => {
        const parsedDate = new Date(dateToFormatValue);

        if (!isValidDate(parsedDate)) {
            return Result.err<string, Error>(new Error(`Invalid date to format: "${dateToFormatValue}"`));
        }

        return Result.ok(
            formatDate(
                parsedDate,
                {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                },
                { locale },
            ),
        );
    });
};

export const formatDateAndTimeByLocale = (options: FormatByLocaleOptions): Result<string, Error> => {
    const { dateStringToFormat, locale } = options;

    const noDateStringProvidedError = Result.err<string, Error>(new Error('No date string provided'));

    return dateStringToFormat.mapOr(noDateStringProvidedError, (dateToFormatValue) => {
        const parsedDate = new Date(dateToFormatValue);

        if (!isValidDate(parsedDate)) {
            return Result.err<string, Error>(new Error(`Invalid date to format: "${dateToFormatValue}"`));
        }

        const formattedDate = formatDate(
            parsedDate,
            {
                day: '2-digit',
                hour: '2-digit',
                hour12: true,
                minute: '2-digit',
                month: '2-digit',
                year: '2-digit',
            },
            { locale },
        );

        return Result.ok(formattedDate);
    });
};

export const createFormatDateForGridValue = (
    locale: LanguageKey,
): ((value: string | undefined) => string | undefined) => {
    return (value) => {
        return formatDateByLocale({
            dateStringToFormat: Maybe.of(value),
            locale,
        }).unwrapOr(undefined);
    };
};
